import { useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { AnimatedShape, Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentsCall } from '@/departments/state/api';

type Props = {
  onDepartmentChoose: ({ id, name }: { id: string; name?: string }) => void;
  onGuestChoose: () => void;
};

export const ChooseTeamsOrGuest = ({ onDepartmentChoose, onGuestChoose }: Props) => {
  const t = useTranslations('settings.tabs.delegates.addDelegate.dialog.chooseTeamsOrGuest');
  const { genericResponsiveValue } = useResponsiveStyle();

  const gaps = convertSizeToNumber(`${useThemeValue('sizes', 's')}`);
  const [isHovered, setIsHovered] = useState<{ id: string } | undefined>(undefined);

  const {
    execute: executeGetDepartments,
    loading: loadingGetDepartments,
    error: errorGetDepartments,
    data,
  } = getDepartmentsCall.hook();

  const isHumanResourceDepartment = (name?: string) => {
    if (!name) return false;
    const keywords = ['risorse umane', 'human resource', 'hr', 'h&r'];
    const normalize = (str: string) => str.toLowerCase();
    return keywords.some(k => normalize(name).includes(k));
  };

  useEffect(() => {
    executeGetDepartments({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box width='100%'>
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='p2/light' color='grey/light' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>

      <Box
        mb='xs'
        mt='m'
        width='100%'
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: { desktop: '1fr 1fr' },
          defaultValue: '1fr',
        })}
        style={{ gap: gaps }}
        alignItems='stretch'
      >
        {!loadingGetDepartments &&
          data
            ?.slice()
            .sort((a, b) => {
              const aMatch = isHumanResourceDepartment(a.name);
              const bMatch = isHumanResourceDepartment(b.name);
              if (aMatch && !bMatch) return -1;
              if (!aMatch && bMatch) return 1;
              return 0;
            })
            .map(department => (
              <Box
                key={department.id}
                onMouseEnter={() => setIsHovered({ id: department.id ?? '' })}
                onMouseLeave={() => setIsHovered(undefined)}
              >
                <Card
                  bg={
                    isHumanResourceDepartment(department.name) || isHovered?.id === department.id
                      ? 'primary'
                      : 'grey/dark/background'
                  }
                  onClick={() =>
                    department.id
                      ? onDepartmentChoose({
                          id: department.id,
                          name: department.name,
                        })
                      : null
                  }
                  height='100%'
                  style={{ cursor: 'pointer' }}
                >
                  <Typography variant='p1/bold'>
                    {department.name?.toLowerCase() === 'unknown'
                      ? t('unknownDepartment')
                      : department.name}
                  </Typography>
                  <Typography variant='p2/light'>{`${t('listOfTeamsLabel')} ${(department.teams?.filter(team => team !== 'unknown')?.length || 0) > 0 ? department.teams?.filter(team => team !== 'unknown')?.join(', ') : '-'}`}</Typography>
                </Card>
              </Box>
            ))}
        {loadingGetDepartments && (
          <>
            {new Array(4).fill(0).map(() => (
              <Card key={Math.random()} bg='primary/background'>
                <Typography variant='p1/bold'>
                  <AnimatedShape width='30%' height={20} />
                </Typography>
                <Typography variant='p2/light' mt='xxs'>
                  <AnimatedShape width='45%' height={15} />
                </Typography>
              </Card>
            ))}
          </>
        )}
      </Box>
      {!loadingGetDepartments && errorGetDepartments && (
        <AlertBadge type='warning' label={t('noDepartmentsFound')} />
      )}

      <Box display='flex' flexDirection='column' textAlign='center' mt='m'>
        <Typography mt='none' variant='p2/light' color='primary/text'>
          {`${t('addDelegateGuestQuestion')} `}
          <Typography
            as='span'
            style={{ textDecoration: 'underline' }}
            onClick={onGuestChoose}
            cursor='pointer'
          >
            {t('addDelegateGuestCta')}
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};
